import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trash2, ExternalLink, Edit3 } from "lucide-react";
import DashboardLayout from "@/components/app/DashboardLayout";
import { Button } from "@/components/ui/button";
import { MenuRenderer } from "@/components/app/MenuRenderer";
import { NewDesignButton, createBlankDesign } from "@/pages/Templates";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const headingFont = { fontFamily: "Outfit, sans-serif" };
const accentFont  = { fontFamily: "'Playfair Display', serif", fontStyle: "italic", fontWeight: 500 };

// Mini design thumbnail — measures its own container so the miniature stays
// perfectly filled regardless of the parent column width or the design's
// aspect ratio (16:9 vs 9:16). Extracted so both grid + rail views can reuse.
function DesignThumb({ config }) {
  const cw = config?.canvas_w || 1920;
  const ch = config?.canvas_h || 1080;
  const ratio = `${cw} / ${ch}`;
  const ref = useRef(null);
  const [scale, setScale] = useState(0.2);
  useLayoutEffect(() => {
    if (!ref.current) return;
    const el = ref.current;
    const update = () => {
      const w = el.clientWidth;
      if (w > 0) setScale(w / cw);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [cw]);
  return (
    <div ref={ref} className="relative overflow-hidden bg-slate-100" style={{ aspectRatio: ratio }}>
      {config && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ transformOrigin: "0 0", transform: `scale(${scale})`, width: `${cw}px`, height: `${ch}px` }}
        >
          <MenuRenderer config={config} scale="tv" />
        </div>
      )}
    </div>
  );
}

export default function Designs() {
  const navigate = useNavigate();
  const [designs, setDesigns] = useState([]);
  const load = () => api.get("/designs").then((r) => setDesigns(r.data.designs || []));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this design?")) return;
    try {
      await api.delete(`/designs/${id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6 md:p-10 max-w-7xl text-slate-900">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-sky-600 mb-3">My designs</div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] font-semibold" style={headingFont}>
              Your{" "}
              <span style={accentFont} className="text-sky-600">saved</span>{" "}
              menus &amp; ads
            </h1>
          </div>
          <NewDesignButton
            onPick={(o) => createBlankDesign(o, navigate)}
            variant="pill"
            label="+ New design"
          />
        </div>

        {designs.length === 0 ? (
          <div className="rounded-3xl p-12 border border-dashed text-center" style={{ background: "#F8FAFC", borderColor: "#E2E8F0" }} data-testid="empty-designs">
            <Edit3 className="h-10 w-10 mx-auto text-slate-400 mb-4" />
            <h3 className="text-xl font-semibold mb-2" style={headingFont}>No designs yet</h3>
            <p className="text-sm text-slate-500 max-w-sm mx-auto mb-5">Choose a template to create your first menu or ad in under a minute.</p>
            <Link to="/dashboard/templates">
              <Button className="rounded-full bg-sky-500 hover:bg-sky-600 text-white shadow-sm">Browse templates</Button>
            </Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 items-start" data-testid="designs-grid">
            {designs.map((d) => (
              <div
                key={d.id}
                className="rounded-3xl border backdrop-blur-xl overflow-hidden flex flex-col hover:-translate-y-0.5 hover:border-sky-300 transition-all"
                style={{ background: "#FFFFFF", borderColor: "#E2E8F0" }}
                data-testid={`design-${d.id}`}
              >
                <DesignThumb config={d.config} />
                <div className="p-5 flex-1 flex flex-col">
                  <h3 className="font-semibold text-lg truncate" style={headingFont}>{d.name}</h3>
                  <div className="text-xs text-slate-500 mt-1">Updated {new Date(d.updated_at).toLocaleString()}</div>
                  <div className="flex gap-2 mt-4">
                    <Link to={`/dashboard/designs/${d.id}`} className="flex-1">
                      <Button variant="outline" className="rounded-full w-full bg-slate-50 border-slate-200 text-slate-900 hover:bg-slate-100" data-testid={`edit-design-${d.id}`}>
                        <Edit3 className="h-4 w-4 mr-1" />Edit
                      </Button>
                    </Link>
                    <Link to={`/preview/${d.id}`} target="_blank">
                      <Button variant="outline" className="rounded-full bg-slate-50 border-slate-200 text-slate-900 hover:bg-slate-100" data-testid={`preview-design-${d.id}`}>
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    </Link>
                    <Button variant="outline" className="rounded-full bg-slate-50 border-slate-200 text-rose-500 hover:bg-rose-50 hover:text-rose-700" onClick={() => remove(d.id)} data-testid={`delete-design-${d.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
